'use client'
import "../BookInteractionPill/book-interaction-pill.css"
import { track } from '@vercel/analytics';
import { isbnToAsin } from "../../books/utils/formatters";

import { useState, useEffect } from "react";

type BuyButtonProps = {
  isbn?: string;
  asid?: string;
  openPopout: string | null;
  setOpenPopout: React.Dispatch<React.SetStateAction<string | null>>;
};

// Mapping of country-specific Amazon and bookstore links
const COUNTRY_LINKS = {
  US: {
    amazon: (asin: string) => `https://www.amazon.com/dp/${asin}`,
    bookstore: (isbn: string) => `https://bookshop.org/book/${isbn}`,
    bookstoreLabel: 'Buy on Bookshop.org'
  },
  DE: {
    amazon: (asin: string) => `https://www.amazon.de/dp/${asin}`,
    bookstore: (isbn: string) => `https://www.kulturkaufhaus.de/en/detail/ISBN-${isbn}`,
    bookstoreLabel: 'Buy on Dussmann'
  }
};

export default function BuyButton({ isbn, asid, openPopout, setOpenPopout }: BuyButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [country, setCountry] = useState('US'); // Default to US

  useEffect(() => {
    setIsOpen(openPopout === "buy");
  }, [openPopout]);

  useEffect(() => {
    async function getCountry() {
      try {
        const response = await fetch('/api/geo');
        const data = await response.json();
        if (data.country && COUNTRY_LINKS[data.country as keyof typeof COUNTRY_LINKS]) {
          setCountry(data.country);
        } else {
          // Keep default US if country is not in our mapping
          setCountry('US');
        }
      } catch (error) {
        console.error('Error fetching country:', error);
        // Keep the default US if there's an error
      }
    }
    
    getCountry();
  }, []);

  const handleClick = () => {
    track(`Buy button clicked`);
    setOpenPopout(isOpen ? null : "buy");
  };

  const finalAsid = asid || (isbn ? isbnToAsin(isbn) : undefined);

  // Determine the links based on the country, default to US if not found
  const countryLinks = COUNTRY_LINKS[country as keyof typeof COUNTRY_LINKS] || COUNTRY_LINKS.US;

  return (
    <div className="buy-button relative ">
      {openPopout === "buy" && isOpen && (
        <div>
          <a href={countryLinks.amazon(finalAsid || '')} target="_blank" rel="noopener noreferrer"> 
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.48364 8.73709L3.69949 3.64011H3.2198C2.98785 3.64011 2.7998 3.45207 2.7998 3.22011C2.7998 2.98815 2.98785 2.80011 3.2198 2.80011H4.05338C4.09366 2.79944 4.13303 2.80459 4.17055 2.81486C4.23016 2.83111 4.28449 2.86017 4.33041 2.89889C4.3799 2.94054 4.41996 2.99369 4.44612 3.05504C4.46054 3.08873 4.47067 3.12469 4.47585 3.16223L4.61398 4.06011H7.8398V4.90011H4.74322L5.26014 8.26011H9.20731L9.83731 6.16011H10.7143L9.92209 8.80079C9.8688 8.97845 9.70528 9.10011 9.5198 9.10011H4.90643C4.86676 9.10078 4.82798 9.09582 4.79097 9.08587C4.72792 9.069 4.67071 9.03782 4.62304 8.99603C4.57939 8.95782 4.54346 8.9105 4.51849 8.85641C4.50128 8.81925 4.48934 8.77916 4.48364 8.73709Z" fill="#EEEEEE"/>
              <path d="M6.1598 10.3601C6.1598 10.824 5.78372 11.2001 5.3198 11.2001C4.85589 11.2001 4.4798 10.824 4.4798 10.3601C4.4798 9.89619 4.85589 9.52011 5.3198 9.52011C5.78372 9.52011 6.1598 9.89619 6.1598 10.3601Z" fill="#EEEEEE"/>
              <path d="M9.9398 10.3601C9.9398 10.824 9.56372 11.2001 9.0998 11.2001C8.63589 11.2001 8.2598 10.824 8.2598 10.3601C8.2598 9.89619 8.63589 9.52011 9.0998 9.52011C9.56372 9.52011 9.9398 9.89619 9.9398 10.3601Z" fill="#EEEEEE"/>
              <path d="M9.9398 2.80011C10.1718 2.80011 10.3598 2.98815 10.3598 3.22011V3.64011H10.7798C11.0118 3.64011 11.1998 3.82815 11.1998 4.06011C11.1998 4.29207 11.0118 4.48011 10.7798 4.48011H10.3598V4.90011C10.3598 5.13207 10.1718 5.32011 9.9398 5.32011C9.70785 5.32011 9.5198 5.13207 9.5198 4.90011V4.48011H9.0998C8.86784 4.48011 8.6798 4.29207 8.6798 4.06011C8.6798 3.82815 8.86784 3.64011 9.0998 3.64011H9.5198V3.22011C9.5198 2.98815 9.70785 2.80011 9.9398 2.80011Z" fill="#EEEEEE"/>
            </svg> Buy on Amazon 
          </a>
          <a href={countryLinks.bookstore(isbn || '')} target="_blank" rel="noopener noreferrer">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.48364 8.73709L3.69949 3.64011H3.2198C2.98785 3.64011 2.7998 3.45207 2.7998 3.22011C2.7998 2.98815 2.98785 2.80011 3.2198 2.80011H4.05338C4.09366 2.79944 4.13303 2.80459 4.17055 2.81486C4.23016 2.83111 4.28449 2.86017 4.33041 2.89889C4.3799 2.94054 4.41996 2.99369 4.44612 3.05504C4.46054 3.08873 4.47067 3.12469 4.47585 3.16223L4.61398 4.06011H7.8398V4.90011H4.74322L5.26014 8.26011H9.20731L9.83731 6.16011H10.7143L9.92209 8.80079C9.8688 8.97845 9.70528 9.10011 9.5198 9.10011H4.90643C4.86676 9.10078 4.82798 9.09582 4.79097 9.08587C4.72792 9.069 4.67071 9.03782 4.62304 8.99603C4.57939 8.95782 4.54346 8.9105 4.51849 8.85641C4.50128 8.81925 4.48934 8.77916 4.48364 8.73709Z" fill="#EEEEEE"/>
              <path d="M6.1598 10.3601C6.1598 10.824 5.78372 11.2001 5.3198 11.2001C4.85589 11.2001 4.4798 10.824 4.4798 10.3601C4.4798 9.89619 4.85589 9.52011 5.3198 9.52011C5.78372 9.52011 6.1598 9.89619 6.1598 10.3601Z" fill="#EEEEEE"/>
              <path d="M9.9398 10.3601C9.9398 10.824 9.56372 11.2001 9.0998 11.2001C8.63589 11.2001 8.2598 10.824 8.2598 10.3601C8.2598 9.89619 8.63589 9.52011 9.0998 9.52011C9.56372 9.52011 9.9398 9.89619 9.9398 10.3601Z" fill="#EEEEEE"/>
              <path d="M9.9398 2.80011C10.1718 2.80011 10.3598 2.98815 10.3598 3.22011V3.64011H10.7798C11.0118 3.64011 11.1998 3.82815 11.1998 4.06011C11.1998 4.29207 11.0118 4.48011 10.7798 4.48011H10.3598V4.90011C10.3598 5.13207 10.1718 5.32011 9.9398 5.32011C9.70785 5.32011 9.5198 5.13207 9.5198 4.90011V4.48011H9.0998C8.86784 4.48011 8.6798 4.29207 8.6798 4.06011C8.6798 3.82815 8.86784 3.64011 9.0998 3.64011H9.5198V3.22011C9.5198 2.98815 9.70785 2.80011 9.9398 2.80011Z" fill="#EEEEEE"/>
            </svg> {countryLinks.bookstoreLabel}
          </a>
        </div>
      )}
      <button onClick={handleClick}>
        Buy Book
      </button>
    </div>
  );
};